import { forceRefreshAccessToken, getAccessToken, logoutUser } from 'utils/jwt';

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public details: string[] = [],
  ) {
    super(message);
  }
}

async function buildHeaders(isForm = false): Promise<HeadersInit> {
  const token = await getAccessToken();
  if (!token) {
    logoutUser();
    throw new ApiError('Session expirée', 401);
  }
  return {
    Authorization: `Bearer ${token}`,
    'x-current-language': 'fr',
    ...(!isForm ? { 'Content-Type': 'application/json' } : {}),
  };
}

async function send(serviceUrl: string, path: string, init: RequestInit, isForm: boolean): Promise<Response> {
  const headers = await buildHeaders(isForm);
  return fetch(`${serviceUrl}/${path}`, { headers, ...init });
}

async function request<T>(serviceUrl: string, path: string, init: RequestInit, isForm = false): Promise<T> {
  let res = await send(serviceUrl, path, init, isForm);

  // Un 401 après un long transfert signifie le plus souvent que le jeton a expiré pendant l'envoi :
  // le serveur ne le vérifie qu'à la réception complète. On renouvelle et on rejoue une seule fois ;
  // le serveur n'a rien traité, le rejeu est sans effet de bord.
  if (res.status === 401 && (await forceRefreshAccessToken())) {
    res = await send(serviceUrl, path, init, isForm);
  }

  // Toujours refusé : la session est close et l'appelant doit le savoir. Retourner une valeur vide
  // ferait passer l'appel pour réussi.
  if (res.status === 401) {
    logoutUser();
    throw new ApiError('Session expirée', 401);
  }

  const body = await res.json().catch(() => null);
  if (!res.ok) {
    throw new ApiError(body?.message ?? body?.error ?? `Erreur ${res.status}`, res.status, body?.errors ?? []);
  }
  return body as T;
}

export const api = {
  get: <T>(serviceUrl: string, path: string) => request<T>(serviceUrl, path, { method: 'GET' }),
  post: <T>(serviceUrl: string, path: string, body: unknown) =>
    request<T>(serviceUrl, path, { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(serviceUrl: string, path: string, body?: unknown) =>
    request<T>(serviceUrl, path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(serviceUrl: string, path: string, body: unknown) =>
    request<T>(serviceUrl, path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(serviceUrl: string, path: string) => request<T>(serviceUrl, path, { method: 'DELETE' }),
  postForm: <T>(serviceUrl: string, path: string, formData: FormData) =>
    request<T>(serviceUrl, path, { method: 'POST', body: formData }, true),
  putForm: <T>(serviceUrl: string, path: string, formData: FormData) =>
    request<T>(serviceUrl, path, { method: 'PUT', body: formData }, true),
};
